/** Personal lesson session page — auto-creates and plays a session for an AI-generated lesson. */
import { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSessionLauncher, SessionPlayer, Interstitial } from '.';
import LoadingIcon from '@/foundation/ui/LoadingIcon';

/**
 * Route component for `/lessons/:folderSlug/:lessonId`.
 *
 * Auto-creates a learn session for the given personal lesson if no active
 * session exists. Delegates all session lifecycle logic to useSessionLauncher.
 */
export default function PersonalLessonPage() {
  const { lessonId, folderSlug } = useParams<{ lessonId: string; folderSlug: string }>();
  const navigate = useNavigate();

  const onExit = useCallback(
    () => navigate(folderSlug && folderSlug !== 'unfiled' ? `/lessons/${folderSlug}` : '/lessons'),
    [navigate, folderSlug]
  );

  const {
    session,
    sessionReady,
    loading,
    createError,
    submitFn,
    sessionType,
    startIndex,
    sortedSteps,
    confidencePositions,
    paasPositions,
    handleComplete,
    handleExit,
  } = useSessionLauncher({ lessonId, onExit });

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div
        className="flex min-h-dvh items-start justify-center pt-[28vh]"
        style={{ background: 'var(--color-bg)' }}
      >
        <LoadingIcon size="lg" />
      </div>
    );
  }

  // ── Error / no session ───────────────────────────────────────────────────

  if (!sessionReady) {
    return (
      <Interstitial
        title={createError ? 'Couldn\u2019t start the session' : 'No active session'}
        description={
          createError
            ? 'The lesson may still be generating. Head back and try again in a moment.'
            : 'Something went wrong loading this lesson.'
        }
        primaryLabel="Go home"
        onPrimary={onExit}
      />
    );
  }

  // ── Session player ───────────────────────────────────────────────────────

  return (
    <SessionPlayer
      key={session!.id}
      sessionId={session!.id}
      steps={sortedSteps}
      mode={sessionType}
      startIndex={startIndex}
      confidencePositions={confidencePositions}
      paasPositions={paasPositions}
      onExit={handleExit}
      onComplete={handleComplete}
      submitFn={submitFn}
      isPersonalLesson
    />
  );
}
